import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/lib/toast'
import i18n from '@/i18n'
import { api } from '@/lib/api'
import type { Reminder, ReminderListResponse } from '@/lib/types'

export function useContactReminders(contactId?: string) {
  return useQuery({
    queryKey: ['reminders', 'contact', contactId],
    queryFn: () => api<ReminderListResponse>(`/api/reminders?contactId=${contactId}&limit=100`),
    enabled: !!contactId,
  })
}

export function useReminders(page = 1, type?: string, upcoming?: boolean) {
  const params = new URLSearchParams({ page: String(page) })
  if (type) params.set('type', type)
  if (upcoming) params.set('upcoming', 'true')

  return useQuery({
    queryKey: ['reminders', page, type, upcoming],
    queryFn: () => api<ReminderListResponse>(`/api/reminders?${params}`),
  })
}

export function useCreateReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      contact_id?: string
      type: string
      title: string
      remind_at: string
      recurring?: string
      recurrence_interval?: number
      recurrence_end_date?: string | null
    }) => api<Reminder>('/api/reminders', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(i18n.t('toast.reminderCreated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useUpdateReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      title?: string
      remind_at?: string
      recurring?: string
      recurrence_interval?: number
      recurrence_end_date?: string | null
      sent?: boolean
    }) => api<Reminder>(`/api/reminders/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(i18n.t('toast.reminderUpdated'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}

export function useDeleteReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/reminders/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reminders'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(i18n.t('toast.reminderDeleted'))
    },
    onError: () => {
      toast.error(i18n.t('toast.error'))
    },
  })
}
